import { useMemo } from "react";
import { Clock, MapPin, ArrowRight, Truck } from "lucide-react";

export default function TransportTimeline({ requests, sites }) {
  const timeline = useMemo(() => {
    const transportReqs = requests
      .filter(r => r.request_type === "TRANSPORT")
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    // Group by machine
    const byMachine = {};
    transportReqs.forEach(r => {
      const key = r.machine_number || r.machine_id;
      if (!byMachine[key]) byMachine[key] = [];
      byMachine[key].push(r);
    });

    return Object.entries(byMachine).map(([machineNum, reqs]) => ({
      machine: machineNum,
      events: reqs.map(r => {
        const fromSite = sites.find(s => s.id === r.site_id);
        return {
          id: r.id,
          from: fromSite?.name || r.site_name || "Unknown",
          to: r.target_site_name || "Unknown",
          status: r.status,
          driver: r.assigned_driver || null,
          date: r.created_at,
        };
      }),
    }));
  }, [requests, sites]);

  if (timeline.length === 0) {
    return (
      <div className="p-8 border border-dashed border-slate-200 rounded-xl text-center">
        <Clock className="w-6 h-6 text-slate-300 mx-auto mb-2" />
        <p className="text-xs font-bold text-slate-400 uppercase">No transport history yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="transport-timeline">
      {timeline.map(({ machine, events }) => (
        <div key={machine} className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden" data-testid={`timeline-${machine}`}>
          <div className="px-4 py-3 bg-white border-b border-slate-200 flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center text-white">
              <Truck className="w-3.5 h-3.5" />
            </div>
            <span className="text-sm font-black text-slate-900 uppercase tracking-tight font-mono">{machine}</span>
            <span className="text-[10px] font-bold text-slate-400 ml-auto">{events.length} transport{events.length !== 1 ? "s" : ""}</span>
          </div>
          <div className="px-4 py-3">
            <div className="relative pl-6">
              {/* Vertical line */}
              <div className="absolute left-[9px] top-2 bottom-2 w-[2px] bg-blue-200" />
              {events.map((ev, i) => {
                const statusColor = ev.status === "DONE" ? "bg-lime-500" : ev.status === "IN_PROGRESS" ? "bg-amber-400" : "bg-blue-500";
                const dt = new Date(ev.date);
                const dateStr = dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
                const timeStr = dt.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
                return (
                  <div key={ev.id} className={`relative pb-4 ${i === events.length - 1 ? "pb-0" : ""}`} data-testid={`timeline-event-${ev.id}`}>
                    {/* Dot */}
                    <div className={`absolute -left-6 top-1 w-[18px] h-[18px] rounded-full ${statusColor} border-2 border-white shadow-sm flex items-center justify-center`}>
                      {ev.status === "DONE" && <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
                    </div>
                    {/* Content */}
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 text-xs">
                          <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                          <span className="font-bold text-slate-700 truncate">{ev.from}</span>
                          <ArrowRight className="w-3 h-3 text-blue-400 shrink-0" />
                          <span className="font-bold text-blue-600 truncate">{ev.to}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                            ev.status === "DONE" ? "bg-lime-100 text-lime-600" :
                            ev.status === "IN_PROGRESS" ? "bg-amber-100 text-amber-600" :
                            "bg-blue-100 text-blue-600"
                          }`}>{ev.status === "IN_PROGRESS" ? "IN TRANSIT" : ev.status}</span>
                          {ev.driver && <span className="text-[9px] text-slate-400 font-bold">{ev.driver}</span>}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[10px] font-bold text-slate-500">{dateStr}</p>
                        <p className="text-[9px] text-slate-400 font-mono">{timeStr}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
